import React from 'react';

import { formatKpiString, formatDollarsKpi, locale } from '@trqk/utils';

import {
    IconBroadcast,
    IconDollarSign,
    IconFileTrqkMark,
    IconInfoCircle,
    IconX,
    Tooltip,
    LoadingSpinner,
} from '../..';

import {
    ASCAPIntlPerformancesTooltip,
    BulkPaymentsTable,
    Kpi,
} from './components';

import bulkPaymentsIcon from '../../../assets/icons/bulk_payment.svg';

interface Props {
    kpis: ProKpis | null;
    statementInfo: ProStatementInfo | null;
}

export const Kpis = ({ kpis, statementInfo }: Props): JSX.Element => {
    if (kpis === null || statementInfo === null) {
        return (
            <div className="h-full w-full flex justify-center items-center">
                <LoadingSpinner />
            </div>
        );
    }

    const totalEarnings = formatKpiString({
        kpis,
        kpiType: 'earnings',
        asDollars: true,
    });
    const tracksThisStatement = formatKpiString({
        kpis,
        kpiType: 'tracks_this_statement',
        asDollars: false,
    });
    const cueSheetsThisStatement = formatKpiString({
        kpis,
        kpiType: 'cue_sheets_this_statement',
        asDollars: false,
    });
    let performances = formatKpiString({
        kpis,
        kpiType: 'performances',
        asDollars: false,
    });
    const bulkPaymentsTotal =
        (kpis?.earnings_adjustments ?? 0) +
        (kpis?.earnings_proxies ?? 0) +
        (kpis?.earnings_settlements ?? 0) +
        (kpis?.earnings_surveys ?? 0);
    const bulkPayments = formatDollarsKpi(bulkPaymentsTotal);

    const { region, pro } = statementInfo;

    let showASCAPIntlPerformancesTooltip = false;

    if (region === 'international' && pro === 'ASCAP') {
        showASCAPIntlPerformancesTooltip = true;
        performances = 'N/A';
    }

    return (
        <div className="flex justify-between space-x-6">
            <Kpi
                gradientDotColor="quarternary"
                icon={<IconDollarSign width={30} fillColor="quarternary-500" />}
            >
                <>
                    <div className="font-bold" data-cy="kpi-earnings">
                        {totalEarnings}
                    </div>
                    <div>{locale.enUS.STATEMENTS.KPIS.EARNINGS}</div>
                </>
            </Kpi>

            <Kpi
                gradientDotColor="secondary"
                icon={<IconFileTrqkMark width={30} fillColor="secondary-500" />}
            >
                <>
                    <div>
                        <span className="font-bold" data-cy="kpi-tracks">
                            {tracksThisStatement}
                        </span>
                        &nbsp;
                        {(kpis?.tracks_this_statement ?? 0) === 1
                            ? locale.enUS.STATEMENTS.KPIS.TRACK
                            : locale.enUS.STATEMENTS.KPIS.TRACK_PLURAL}
                    </div>
                    <div className="text-tertiary-400 text-sm">
                        <span className="font-bold" data-cy="kpi-cue-sheets">
                            {cueSheetsThisStatement}
                        </span>
                        &nbsp;
                        {(kpis?.cue_sheets_this_statement ?? 0) === 1
                            ? locale.enUS.STATEMENTS.KPIS.CUE_SHEET
                            : locale.enUS.STATEMENTS.KPIS.CUE_SHEET_PLURAL}
                    </div>
                </>
            </Kpi>

            <Kpi
                gradientDotColor="primary"
                icon={<IconBroadcast width={30} fillColor="primary-500" />}
            >
                <>
                    <div
                        data-cy="kpi-performances"
                        className="flex justify-between items-center"
                    >
                        <div className="font-bold">{performances}</div>
                        {showASCAPIntlPerformancesTooltip && (
                            <Tooltip
                                background="light"
                                RenderMessage={ASCAPIntlPerformancesTooltip}
                                position="bottom"
                                maxWidth={300}
                                minWidth={300}
                            >
                                <IconInfoCircle
                                    fillColor="tertiary-400"
                                    width={20}
                                />
                            </Tooltip>
                        )}
                    </div>
                    <div>
                        {(kpis?.performances ?? 0) === 1
                            ? locale.enUS.STATEMENTS.KPIS.PERFORMANCE
                            : locale.enUS.STATEMENTS.KPIS.PERFORMANCE_PLURAL}
                    </div>
                </>
            </Kpi>

            <Kpi
                gradientDotColor="highlight-mediumpurple"
                icon={<img src={bulkPaymentsIcon} alt="Bulk payments icon" />}
            >
                <>
                    <div
                        data-cy="kpi-bulk-payments"
                        className="flex justify-between items-center"
                    >
                        <div className="font-bold">{bulkPayments}</div>
                        <Tooltip
                            background="light"
                            RenderMessage={({
                                close,
                            }: RenderTooltipMessageProps): JSX.Element => (
                                <div className="text-tertiary-500 bg-white">
                                    <div className="flex justify-end px-2 pt-2 mb-2">
                                        <IconX
                                            width={12}
                                            fillColor="tertiary-400"
                                            handleClick={close}
                                        />
                                    </div>
                                    <div className="flex justify-center">
                                        <BulkPaymentsTable kpis={kpis} />
                                    </div>
                                </div>
                            )}
                            position="bottom"
                            maxWidth={275}
                            minWidth={275}
                        >
                            <IconInfoCircle
                                fillColor="tertiary-400"
                                width={20}
                            />
                        </Tooltip>
                    </div>
                    <div>{locale.enUS.STATEMENTS.KPIS.BULK_PAYMENTS}</div>
                </>
            </Kpi>
        </div>
    );
};
